import { medisApi } from '@/lib/api/client';
import { endpoints } from '@/lib/api/endpoints';
import { unwrapMedis, type MedisEnvelope } from '@/lib/medis/envelope';
import { MEDIS_V2_ROUTES } from '@/lib/medis/v2'; // v1<->v2 switch, see lib/medis/v2/client.ts
import { getAppointmentsV2 } from './appointments.v2.api'; // v2 when switched on
import type {
  Appointment,
  AzureAppointmentDto,
  GetAppointmentsResponseDto,
} from '../types/appointment.types';

function mapAppointment(dto: AzureAppointmentDto): Appointment | null {
  const rawStart = dto.StartTime ?? dto.startTime;
  const rawEnd = dto.EndTime ?? dto.endTime;
  if (!rawStart || !rawEnd) return null;
  const startTime = new Date(rawStart);
  const endTime = new Date(rawEnd);
  if (Number.isNaN(startTime.getTime()) || Number.isNaN(endTime.getTime())) return null;
  return {
    startTime,
    endTime,
    physicianId: dto.Physician ?? dto.physician ?? '',
    specialityId: dto.Speciality ?? dto.speciality ?? '',
    locationId: dto.Location ?? dto.location ?? '',
    degree: dto.Degree ?? dto.degree ?? '',
  };
}

export const appointmentsApi = {
  /**
   * All appointments (past + future) for a patient.
   * `POST /GetAppointments` with `{ Patient: <fhirId> }`. Past/future split is
   * done client-side (the API returns everything) — see `useAppointments`.
   */
  async getAppointments(patientFhirId: string): Promise<Appointment[]> {
    if (MEDIS_V2_ROUTES.getAppointments) return getAppointmentsV2(patientFhirId); // v2 when switched on

    const { data } = await medisApi.post<MedisEnvelope<GetAppointmentsResponseDto>>(
      endpoints.medis.getAppointments,
      { Patient: patientFhirId },
    );
    const response = unwrapMedis(data);
    const list = response.appointments ?? response.Appointments ?? [];
    return list.map(mapAppointment).filter((a): a is Appointment => a !== null);
  },
};
